import { Link } from "react-router-dom";
import {
  ArrowUpRight, ArrowRight, Radar, Brain, Network, Boxes, Glasses, Zap,
  Cpu, LayoutDashboard, Orbit, Wifi, MonitorCog, CheckCircle2,
  Sparkles, Workflow, Layers, ShieldCheck, Target, Repeat,
  Factory, HardHat, Truck, Building2, Bolt, Briefcase, ShieldAlert, Warehouse, Users, Stethoscope,
} from "lucide-react";
import {
  IMAGES, INTELLIGENCE_FRAMEWORK, WHAT_WE_BUILD, OUTCOMES,
  CAPABILITY_CHIPS, COMPLEX_USE_CASES, WHY_NGXP,
} from "@/lib/data";

const ICONS = {
  Radar, Brain, Network, Boxes, Glasses, Zap,
  Cpu, LayoutDashboard, Orbit, Wifi, MonitorCog,
  Sparkles, Workflow, Layers, ShieldCheck, Target, Repeat,
  Factory, HardHat, Truck, Building2, Bolt, Briefcase, ShieldAlert, Warehouse, Users, Stethoscope,
};

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* ============= HERO ============= */}
      <section className="relative overflow-hidden bg-white" data-testid="hero-section">
        {/* Background GIF */}
        <img
          src="/bg.gif"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-15 pointer-events-none"
        />
        <div className="absolute inset-0 grid-pattern pointer-events-none" />
        <div className="glow-blob bg-[#7B2CBF]/25 w-[600px] h-[600px] -top-40 -right-32 pulse-glow" />
        <div className="glow-blob bg-[#00A8E8]/20 w-[500px] h-[500px] -bottom-32 -left-20 pulse-glow" />

        <div className="container-ngxp relative z-10 pt-6 md:pt-10 lg:pt-12 pb-14 md:pb-16 lg:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-6">
              <span className="overline" data-testid="hero-overline">Next Generation Intelligence Systems</span>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.05] tracking-tight text-[#0A0B10]">
                Engineering <span className="gradient-text-glow">Self-Evolving</span> Intelligence Systems for Complex Enterprise Operations
              </h1>
              <p className="mt-6 text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl">
                NGXP Technologies combines AI, real-time data, IoT, embedded systems, advanced 3D simulation, XR, digital twins, and AI-native enterprise platforms to help organizations gain real-time visibility, predict risks, automate workflows, optimize performance, and continuously improve operations.
              </p>
              <p className="mt-5 text-base text-gray-500 max-w-2xl leading-relaxed">
                We build intelligent systems that connect real-world operations with digital intelligence — helping enterprises understand what is happening, why it is happening, what may happen next, and what action should be taken.
              </p>
              <div className="mt-9 flex flex-wrap gap-3" data-testid="hero-ctas">
                <Link to="/contact#contact-form" className="btn-primary-gradient" data-testid="hero-cta-primary">
                  Discuss a Strategic Project <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link to="/services" className="btn-secondary-light" data-testid="hero-cta-secondary">
                  Explore Our Capabilities <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="mt-12 grid grid-cols-3 max-w-md gap-6">
                {[
                  { k: "AI-native", v: "Architecture" },
                  { k: "Real-time", v: "Visibility" },
                  { k: "10x", v: "Improvement potential" },
                ].map((s, i) => (
                  <div key={i}>
                    <div className="text-xl md:text-2xl font-display font-bold text-[#0A0B10]">{s.k}</div>
                    <div className="text-xs text-gray-500 mt-1">{s.v}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-tr from-[#00A8E8]/30 via-[#7B2CBF]/30 to-[#FF007F]/30 rounded-[2rem] blur-2xl" />
                <div className="relative rounded-[2rem] overflow-hidden border border-gray-100 shadow-2xl shadow-purple-500/10 blob-float">
                  <video
                    src="/NGXP-LOGO_RESIZED.mp4"
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-[320px] md:h-[380px] object-contain bg-black"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-2xl border border-gray-100 hidden md:block">
                  <div className="flex items-center gap-3">
                    <div className="icon-badge !w-10 !h-10 !rounded-xl !mb-0"><Brain className="w-5 h-5" /></div>
                    <div>
                      <div className="text-xs text-gray-500">Live</div>
                      <div className="text-sm font-semibold">Operational Intelligence</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============= SECTION 2 — Operational Intelligence ============= */}
      <section className="bg-[#F8F9FA] py-14 md:py-20" data-testid="section-operational-intelligence">
        <div className="container-ngxp">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-6 reveal">
              <span className="overline">The Shift</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight text-[#0A0B10]">
                Enterprises Need More Than Software. They Need <span className="gradient-text-glow">Operational Intelligence.</span>
              </h2>
              <p className="mt-6 text-gray-600 leading-relaxed">
                Organizations today manage complex operations across people, assets, workflows, safety, productivity, and decision-making. Yet many still depend on disconnected systems, delayed reports, manual monitoring, and reactive decisions.
              </p>
            </div>
            <div className="lg:col-span-6 reveal">
              <div className="ngxp-card">
                <h3 className="font-display text-xl font-semibold mb-5">Leadership teams need faster answers to:</h3>
                <ul className="space-y-3">
                  {[
                    "What is happening right now?",
                    "Where are the risks?",
                    "What is slowing performance?",
                    "What needs immediate attention?",
                  ].map((q, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-700">
                      <CheckCircle2 className="w-5 h-5 text-[#7B2CBF] mt-0.5 shrink-0" />
                      <span>{q}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 p-5 rounded-xl bg-gradient-to-r from-[#00A8E8]/10 via-[#7B2CBF]/10 to-[#FF007F]/10 border border-[#7B2CBF]/20">
                  <div className="text-xs uppercase tracking-[0.2em] font-bold gradient-text mb-1">The NGXP Answer</div>
                  <p className="text-[#0A0B10] font-medium">NGXP builds intelligence systems that help organizations <span className="font-semibold">see, understand, decide and act</span> faster.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============= SECTION 3 — Connected Systems ============= */}
      <section className="bg-white py-14 md:py-20" data-testid="section-connected-systems">
        <div className="container-ngxp">
          <div className="max-w-4xl mx-auto text-center reveal">
            <span className="overline">Intelligent Operational Ecosystems</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10]">
              NGXP Converts Complex Operations into <span className="gradient-text-glow">Intelligent, Connected, and Continuously Improving Systems</span>
            </h2>
            <p className="mt-6 text-lg text-gray-600 leading-relaxed">
              NGXP Technologies builds future-ready intelligence systems that connect enterprise data, real-world activity, AI intelligence, simulation, digital twins, dashboards, and workflow automation into one unified operational ecosystem.
            </p>
            <p className="mt-5 text-lg text-gray-600 leading-relaxed">
              Our systems are designed to help organizations monitor operations in real time, identify patterns, predict risks, simulate scenarios, guide decisions, automate workflows, and improve over time through real-world usage.
            </p>
            <p className="mt-5 font-display text-xl text-[#0A0B10] font-medium">
              We help organizations move from <span className="text-gray-400">static digital systems</span> to <span className="gradient-text-glow font-semibold">intelligent operational ecosystems.</span>
            </p>
          </div>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 reveal">
            {[
            { img: "/Real-world-data.gif", label: "Real-world data" },
              { img: "/Ai_Intelligence.gif", label: "AI intelligence" },
              { img: "/digital_twin.gif", label: "Digital twins" },
              { img: "/Decision-command_1080.gif", label: "Decision command" },
            ].map((b, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden aspect-square border border-gray-100 group">
                <img src={b.img} alt={b.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B10]/80 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white text-sm font-semibold">{b.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============= SECTION 4 — NGXP Intelligence Framework (6 cards) ============= */}
      <section className="bg-[#F8F9FA] py-14 md:py-20" data-testid="section-framework">
        <div className="container-ngxp">
          <div className="max-w-3xl reveal">
            <span className="overline">Framework</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10]">
              The NGXP <span className="gradient-text-glow">Intelligence Framework</span>
            </h2>
            <p className="mt-5 text-gray-600 text-lg">Six interconnected layers that convert real-world activity into intelligent, continuously improving operations.</p>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {INTELLIGENCE_FRAMEWORK.map((c, i) => {
              const Icon = ICONS[c.icon];
              return (
                <div key={i} className="ngxp-card reveal" data-testid={`framework-card-${i}`}>
                  <div className="flex items-start justify-between">
                    <div className="icon-badge"><Icon className="w-7 h-7" /></div>
                    <span className="text-xs font-bold tracking-[0.18em] uppercase text-gray-400">0{i + 1}</span>
                  </div>
                  <h3 className="font-display text-xl font-semibold mt-1">{c.layer}</h3>
                  <p className="text-sm font-medium gradient-text-glow mt-1">{c.title}</p>
                  <p className="text-gray-600 text-sm mt-4 leading-relaxed">{c.desc}</p>
                  <div className="mt-5 pt-5 border-t border-gray-100">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-gray-400 font-bold mb-1">Outcome</div>
                    <p className="text-sm text-[#0A0B10] font-medium">{c.outcome}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============= SECTION 5 — What We Build (dark) ============= */}
      <section className="bg-[#0A0B10] text-white py-14 md:py-20 relative overflow-hidden" data-testid="section-what-we-build">
        <div className="absolute inset-0 dark-grid-pattern opacity-50 pointer-events-none" />
        <div className="glow-blob bg-[#7B2CBF]/30 w-[600px] h-[600px] -top-40 -right-40 pulse-glow" />
        <div className="container-ngxp relative z-10">
          <div className="max-w-3xl reveal">
            <span className="overline">What We Build</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
              Six categories of <span className="gradient-text-glow">intelligent systems</span> designed to transform operations.
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {WHAT_WE_BUILD.map((s, i) => {
              const Icon = ICONS[s.icon];
              return (
                <div key={i} className="ngxp-glass-card flex flex-col reveal" data-testid={`build-card-${i}`}>
                  <div className="icon-badge-dark"><Icon className="w-7 h-7" /></div>
                  <h3 className="font-display text-xl font-semibold">{s.title}</h3>
                  <p className="text-sm text-white/65 mt-3 leading-relaxed">{s.desc}</p>
                  <ul className="mt-5 space-y-2">
                    {s.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-white/75">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gradient-to-r from-[#7B2CBF] to-[#FF007F] shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============= SECTION 6 — Outcomes ============= */}
      <section className="bg-white py-14 md:py-20" data-testid="section-outcomes">
        <div className="container-ngxp">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5 reveal">
              <span className="overline">Business Impact</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10] leading-tight">
                Designed to Unlock <span className="gradient-text-glow">10x Operational Improvement</span> Potential
              </h2>
              <p className="mt-6 text-gray-600 leading-relaxed">
                Traditional systems support operations. NGXP systems are designed to <span className="text-[#0A0B10] font-medium">understand, optimize, and improve operations over time.</span>
              </p>
              <div className="mt-8 rounded-2xl overflow-hidden border border-gray-100 shadow-xl">
                <img src="/Designed-to-unlock.jpg" alt="Designed to unlock operational improvement" className="w-full h-64 object-cover" />
              </div>
            </div>
            <div className="lg:col-span-7 reveal">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {OUTCOMES.map((o, i) => (
                  <div key={i} className="flex gap-3 items-start p-5 rounded-xl bg-[#F8F9FA] border border-gray-100 hover:border-[#7B2CBF]/30 transition-colors" data-testid={`outcome-${i}`}>
                    <div className="w-9 h-9 shrink-0 rounded-lg bg-gradient-to-br from-[#00A8E8] to-[#7B2CBF] text-white flex items-center justify-center text-xs font-bold">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <p className="text-sm text-[#0A0B10] font-medium leading-relaxed">{o}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============= SECTION 7 — Capabilities Chips ============= */}
      <section className="bg-[#F8F9FA] py-14 md:py-18" data-testid="section-capabilities">
        <div className="container-ngxp">
          <div className="max-w-3xl reveal">
            <span className="overline">Capabilities</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10]">
              Built from <span className="gradient-text-glow">Real Enterprise Challenges</span>
            </h2>
            <p className="mt-5 text-lg text-gray-600 leading-relaxed">
              NGXP's capabilities are shaped by practical, high-complexity enterprise requirements across operations, safety, training, monitoring, reporting, and decision-making.
            </p>
          </div>
          <p className="mt-10 text-xs uppercase tracking-[0.2em] font-bold text-gray-400 reveal">Capability Areas</p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 reveal">
            {CAPABILITY_CHIPS.map((c, i) => (
              <div key={i} className="ngxp-card" data-testid={`chip-${i}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-[#7B2CBF] to-[#FF007F] flex-shrink-0" />
                  <h3 className="font-display text-base font-semibold text-[#0A0B10]">{c.title}</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============= SECTION 8 — Built For ============= */}
      <section className="bg-white py-14 md:py-20" data-testid="section-built-for">
        <div className="container-ngxp grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 reveal">
            <span className="overline">Built For</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10] leading-tight">
              Organizations Managing <span className="gradient-text-glow">Complex Operations</span>
            </h2>
            <p className="mt-6 text-gray-600 leading-relaxed">
              NGXP is designed for organizations where operations are complex, high-value, safety-sensitive, asset-heavy, data-intensive, or difficult to monitor in real time.
            </p>
          </div>
          <div className="lg:col-span-6 reveal">
            <div className="grid grid-cols-2 gap-3">
              {COMPLEX_USE_CASES.map((u, i) => (
                <div key={i} className="ngxp-card !p-5 !rounded-xl" data-testid={`usecase-${i}`}>
                  <div className="text-xs text-gray-400 font-mono">{String(i + 1).padStart(2, "0")}</div>
                  <p className="mt-1 text-sm font-medium text-[#0A0B10]">{u}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============= SECTION 9 — Why NGXP ============= */}
      <section className="bg-[#0A0B10] text-white py-14 md:py-20 relative overflow-hidden" data-testid="section-why-ngxp">
        <div className="absolute inset-0 dark-grid-pattern opacity-40 pointer-events-none" />
        <div className="glow-blob bg-[#FF007F]/20 w-[500px] h-[500px] top-20 -left-40 pulse-glow" />
        <div className="glow-blob bg-[#00A8E8]/20 w-[500px] h-[500px] bottom-0 -right-40 pulse-glow" />
        <div className="container-ngxp relative z-10">
          <div className="max-w-3xl reveal">
            <span className="overline">Why NGXP</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
              Why <span className="gradient-text-glow">NGXP Technologies</span>
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY_NGXP.map((w, i) => {
              const Icon = ICONS[w.icon];
              return (
                <div key={i} className="ngxp-glass-card reveal" data-testid={`why-card-${i}`}>
                  <div className="icon-badge-dark"><Icon className="w-7 h-7" /></div>
                  <h3 className="font-display text-lg font-semibold">{w.title}</h3>
                  <p className="text-sm text-white/65 mt-3 leading-relaxed">{w.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============= SECTION 10 — Intelligent Transformation ============= */}
      <section className="bg-white py-14 md:py-20" data-testid="section-transformation">
        <div className="container-ngxp grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 reveal">
            <div className="relative rounded-3xl overflow-hidden border border-gray-100 shadow-2xl">
              <img src="/intelligent-transformation.jpg" alt="Intelligent transformation" className="w-full h-[420px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0B10]/40 via-transparent to-transparent" />
            </div>
          </div>
          <div className="lg:col-span-6 reveal">
            <span className="overline">The Next Evolution</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10] leading-tight">
              From Digital Transformation to <span className="gradient-text-glow">Intelligent Transformation</span>
            </h2>
            <p className="mt-6 text-gray-600 leading-relaxed">
              Digital transformation helped organizations move from paper to software. The next evolution is intelligent transformation — where systems understand operations, predict risks, guide decisions, automate workflows, and continuously improve performance.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-3">
              {[
                { l: "Yesterday", v: "Paper" },
                { l: "Today", v: "Software" },
                { l: "Tomorrow", v: "Intelligence" },
              ].map((t, i) => (
                <div key={i} className={`p-5 rounded-xl border ${i === 2 ? "bg-gradient-to-br from-[#00A8E8]/10 via-[#7B2CBF]/10 to-[#FF007F]/10 border-[#7B2CBF]/30" : "bg-[#F8F9FA] border-gray-100"}`}>
                  <div className="text-xs font-bold uppercase tracking-[0.18em] text-gray-400">{t.l}</div>
                  <div className={`mt-1 font-display font-semibold ${i === 2 ? "gradient-text" : "text-[#0A0B10]"}`}>{t.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============= SECTION 11 — Final CTA ============= */}
      <section className="relative py-14 md:py-20 overflow-hidden" data-testid="section-final-cta">
        <div className="absolute inset-0 bg-gradient-to-br from-[#00A8E8] via-[#7B2CBF] to-[#FF007F]" />
        <div className="absolute inset-0 dark-grid-pattern opacity-40" />
        <div className="container-ngxp relative z-10 text-center text-white">
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl mx-auto leading-tight">
            Ready to Build Your Next-Generation Intelligence System?
          </h2>
          <p className="mt-6 text-lg text-white/85 max-w-3xl mx-auto leading-relaxed">
            Whether you are planning an AI-native enterprise platform, digital twin, simulation system, operational intelligence solution, immersive training system, or strategic technology partnership, NGXP can help convert complex operational challenges into intelligent, scalable, and future-ready systems.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4" data-testid="final-cta-buttons">
            <Link to="/contact#contact-form" className="inline-flex items-center gap-2 rounded-full bg-white text-[#0A0B10] px-8 py-4 text-sm font-semibold hover:-translate-y-0.5 transition-transform shadow-2xl" data-testid="final-cta-discuss">
              Start a Strategic Discussion <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="btn-secondary-dark !border-white/40 hover:!bg-white/10" data-testid="final-cta-contact">
              Contact NGXP Technologies <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <p className="mt-12 text-sm text-white/70 italic">
            NGXP Technologies — Engineering self-evolving intelligence systems for complex enterprise operations.
          </p>
        </div>
      </section>
    </div>
  );
}
